import { apiClient } from './authService';
import { createSaleResilient } from '../offline/saleQueue';

// Service methods
const saleService = {
  // Create new sale.
  //
  // Routed through the offline-resilient path: when the shop's internet is
  // down the sale is stamped with a UUID, saved on the phone, and synced to the
  // VPS automatically once the connection returns (idempotent on the server, so
  // no duplicates). Online, it behaves exactly as before and returns the saved
  // sale. Offline, it returns { offline: true, id: null, client_uuid } — callers
  // must handle the missing id (no receipt to open yet). A server-side
  // rejection (e.g. validation) still throws, as before.
  createSale: async (saleData) => {
    return createSaleResilient(saleData);
  },
  
  // Get all sales with optional filter
  getSales: async (params = {}) => {
    try {
      const response = await apiClient.get('sales/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw error.response?.data || { error: 'Failed to fetch sales' };
    }
  },
  

  // Server-side paginated list for SalesList UI.
  getSalesPage: async ({ page = 1, pageSize = 50, search = '', status = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      const response = await apiClient.get('sales/?' + params.toString());
      const data = response.data;
      if (Array.isArray(data)) return { results: data, count: data.length };
      return { results: data?.results || [], count: data?.count || 0 };
    } catch (error) {
      console.error('Error fetching sales page:', error);
      throw error.response?.data || { error: 'Failed to fetch sales' };
    }
  },

  // Get a single sale by ID
  getSaleById: async (id) => {
    try {
      const response = await apiClient.get(`sales/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching sale #${id}:`, error);
      throw error.response?.data || { error: 'Failed to fetch sale details' };
    }
  },
  
  // Get sales history for a specific customer
  getCustomerSalesHistory: async (customerId) => {
    try {
      const response = await apiClient.get(`sales/customer/${customerId}/history/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching sales history for customer #${customerId}:`, error);
      throw error.response?.data || { error: 'Failed to fetch sales history' };
    }
  },
  
  // Record a payment against a sale's outstanding credit (existing endpoint:
  // PATCH sales/:id/payment/ with { payment_amount }).
  updateSalePayment: async (id, { amount }) => {
    try {
      const response = await apiClient.patch(`sales/${id}/payment/`, { payment_amount: amount });
      return response.data;
    } catch (error) {
      console.error(`Error recording payment for sale #${id}:`, error);
      throw error.response?.data || { error: 'Failed to record payment' };
    }
  },

  // Get receipt for a sale
  getSaleReceipt: async (id) => {
    try {
      const response = await apiClient.get(`sales/${id}/receipt/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching receipt for sale #${id}:`, error);
      throw error.response?.data || { error: 'Failed to fetch receipt' };
    }
  },
  
  // Delete a sale (if allowed)
  deleteSale: async (id) => {
    try {
      await apiClient.delete(`sales/${id}/`);
      return true;
    } catch (error) {
      console.error(`Error deleting sale #${id}:`, error);
      throw error.response?.data || { error: 'Failed to delete sale' };
    }
  },
  
  // Get sales statistics
  getSalesStatistics: async (params = {}) => {
    try {
      const response = await apiClient.get('sales/statistics/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching sales statistics:', error);
      throw error.response?.data || { error: 'Failed to fetch sales statistics' };
    }
  }
};

export default saleService;
